import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL!;

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get token from Zustand persist storage
    const authStorage = localStorage.getItem('auth-storage');
    let token = null;
    
    if (authStorage) {
      try {
        const parsedAuth = JSON.parse(authStorage);
        token = parsedAuth.state?.token;
      } catch (e) {
        // Fallback to old token storage for backward compatibility
        token = localStorage.getItem('token');
      }
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on 401 for login/register endpoints (invalid credentials)
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') || 
                          error.config?.url?.includes('/auth/register');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Clear Zustand auth storage
      localStorage.removeItem('auth-storage');
      // Also clear old storage for backward compatibility
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data: { first_name: string; last_name: string; email: string; password: string }) =>
    api.post('/auth/register', data),
  
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  
  logout: () => api.post('/auth/logout'),
  
  getProfile: () => api.get('/auth/profile'),
  
  updateProfile: (data: { first_name?: string; last_name?: string }) =>
    api.put('/auth/profile', data),
  
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/auth/change-password', data),
};

// Meeting API
export interface CreateMeetingRequest {
  title: string; 
  description?: string; 
  is_recording_enabled?: boolean;
  is_screen_sharing_enabled?: boolean;
  is_chat_enabled?: boolean;
}

// New interfaces for refactored participant/session structure
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  username: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: User;
  };
}

export interface MeetingParticipant {
  id: string;
  meeting_id: string;
  user_id: string;
  user_name: string;
  is_host: boolean;
  created_at: string;
  updated_at: string;
  user?: User;
  sessions?: MeetingSession[];
}

export interface MeetingSession {
  id: string;
  meeting_id: string;
  participant_id: string;
  is_camera_enabled: boolean;
  is_microphone_enabled: boolean;
  status: 'joined' | 'left' | 'kicked';
  joined_at: string;
  left_at?: string;
  created_at: string;
  updated_at: string;
}

export interface MeetingWithParticipants {
  id: string;
  title: string;
  description?: string;
  status: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  user?: User;
  participant_count: number;
  participants: MeetingParticipant[];
}

export interface MeetingParticipantWithJoinLink {
  id: string;
  user_id: string;
  user_name: string;
  is_host: boolean;
  token: string;
  join_link: string;
  created_at: string;
  updated_at: string;
}

export interface AddParticipantRequest {
  user_name: string;
  is_host?: boolean;
}

export interface UpdateParticipantRequest {
  is_host?: boolean;
}

export interface MeetingParticipantsResponse {
  meeting: {
    id: string;
    title: string;
    description?: string;
  };
  participants: MeetingParticipantWithJoinLink[];
}

export const meetingAPI = {
  create: (data: CreateMeetingRequest) =>
    api.post('/meetings', data),
  
  list: () => api.get('/meetings'),
  
  get: (id: string) => api.get(`/meetings/${id}`),
  
  join: (id: string, data: { participant_name: string; camera_enabled?: boolean; microphone_enabled?: boolean }) => api.post(`/meetings/${id}/join`, data),
  
  leave: (id: string) => api.post(`/meetings/${id}/leave`),
  
  delete: (id: string) => api.delete(`/meetings/${id}`),
  
  getParticipants: (id: string) => api.get(`/meetings/${id}/participants`),
  
  updateMediaState: (id: string, data: { camera_enabled?: boolean; microphone_enabled?: boolean }) => 
    api.put(`/meetings/${id}/media-state`, data),

  // Participant management for dashboard
  getMeetingParticipants: (id: string) =>
    api.get(`/meetings/${id}/manage-participants`),

  addParticipant: (id: string, data: AddParticipantRequest) =>
    api.post(`/meetings/${id}/manage-participants`, data),

  updateParticipant: (id: string, participantId: string, data: UpdateParticipantRequest) =>
    api.put(`/meetings/${id}/manage-participants/${participantId}`, data),

  deleteParticipant: (id: string, participantId: string) =>
    api.delete(`/meetings/${id}/manage-participants/${participantId}`),

  // Recording API
  startRecording: (id: string, data: { layout?: string; audio_only?: boolean; width?: number; height?: number; show_participant_labels?: boolean; custom_template?: string }) =>
    api.post(`/meetings/${id}/recording/start`, data),

  stopRecording: (id: string, recordingId: string) =>
    api.post(`/meetings/${id}/recording/stop`, { recording_id: recordingId }),

  getRecordingStatus: (id: string, recordingId: string) =>
    api.get(`/meetings/${id}/recording/status?recording_id=${recordingId}`),

  getRecordings: (id: string) =>
    api.get(`/meetings/${id}/recordings`),

  deleteRecording: (meetingId: string, recordingId: string) =>
    api.delete(`/meetings/${meetingId}/recordings/${recordingId}`),

  getById: (id: string) =>
    api.get(`/meetings/${id}`),
};

// Message API
export const messageAPI = {
  getMessages: (meetingId: string) => api.get(`/meetings/${meetingId}/messages`),
  
  // Updated to match new backend schema: type ('text' | 'system') with participant-based structure
  sendMessage: (
    meetingId: string,
    data: { content: string; type?: 'text' | 'system' }
  ) =>
    api.post(`/meetings/${meetingId}/messages`, {
      content: data.content,
      type: data.type ?? 'text',
    }),
  
  deleteMessage: (meetingId: string, messageId: string) =>
    api.delete(`/meetings/${meetingId}/messages/${messageId}`),
};

// LiveStream API
export const livestreamAPI = {
  create: (data: {
    title: string;
    description?: string;
    type?: 'video' | 'audio' | 'video_audio';
    is_recording_enabled?: boolean;
    is_chat_enabled?: boolean;
    scheduled_at?: string;
    stream_settings?: {
      video_quality?: 'low' | 'medium' | 'high' | 'ultra';
      audio_quality?: 'low' | 'medium' | 'high';
      enable_screen_share?: boolean;
      enable_chat?: boolean;
    };
  }) => api.post('/livestreams', data),
  
  list: (params?: { status?: string; type?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.type) queryParams.append('type', params.type);
    const query = queryParams.toString();
    return api.get(`/livestreams${query ? `?${query}` : ''}`);
  },
  
  get: (id: string) => api.get(`/livestreams/${id}`),
  
  getByRoom: (roomName: string) => api.get(`/livestreams/room/${roomName}`),
  
  joinStream: (roomName: string, data: { viewer_name: string }) => 
    api.post(`/livestreams/room/${roomName}/join`, data),
  
  leaveStream: (roomName: string) => 
    api.post(`/livestreams/room/${roomName}/leave`),
  
  startPublishing: (roomName: string) => 
    api.post(`/livestreams/room/${roomName}/publish`),
  
  endStream: (id: string) => api.post(`/livestreams/${id}/end`),
  
  delete: (id: string) => api.delete(`/livestreams/${id}`),
  
  getSessions: (id: string) => api.get(`/livestreams/${id}/viewers`),
  
  generateToken: (data: {
    room: string;
    identity: string;
    is_publisher?: boolean;
  }) => api.post('/livestreams/token', data),

  // Generate app-free public token (authenticated)
  generateAppFreeToken: (data: {
    livestream_id: string;
    permissions?: {
      can_publish?: boolean;
      can_view?: boolean;
    };
    expires_in_hours?: number;
  }) => api.post('/livestreams/generate-app-free-token', data),

  // Recording management
  getRecordings: (id: string) => api.get(`/livestreams/${id}/recordings`),
  deleteRecording: (id: string, recordingId: string) => api.delete(`/livestreams/${id}/recordings/${recordingId}`),
};

// User Apps API
export interface UserApp {
  id: string;
  name: string;
  description?: string;
  secret_key: string;
  user_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const appsAPI = {
  // List all apps for authenticated user
  list: () => api.get<{ success: boolean; data: UserApp[] }>('/apps'),
  
  // Get specific app by ID
  get: (appId: string) => api.get<{ success: boolean; data: UserApp }>(`/apps/${appId}`),
  
  // Create new app
  create: (data: { name: string; description?: string }) =>
    api.post<{ success: boolean; data: UserApp; message: string }>('/apps', data),
  
  // Update app name and description
  update: (appId: string, data: { name: string; description?: string }) =>
    api.put<{ success: boolean; data: UserApp; message: string }>(`/apps/${appId}`, data),
  
  // Regenerate secret key
  regenerateSecret: (appId: string) =>
    api.post<{ success: boolean; data: UserApp; message: string }>(`/apps/${appId}/regenerate-secret`),
  
  // Delete app
  delete: (appId: string) =>
    api.delete<{ success: boolean; message: string }>(`/apps/${appId}`)
};

// Public API (no authentication required)
export const publicAPI = {
  // Validate public token
  validateToken: (token: string) =>
    axios.get<{ success: boolean; data: any; message?: string }>(`${API_BASE_URL}/v1/livestreams/token/${token}/validate`),
  
  // Generate public token (requires app credentials in headers)
  generateToken: (data: { livestream_id: string; permissions?: any; expires_in?: number }, appId: string, appSecret: string) =>
    axios.post<{ success: boolean; data: any; message?: string }>(
      `${API_BASE_URL}/v1/livestreams/token`,
      data,
      {
        headers: {
          'App-Id': appId,
          'App-Secret': appSecret,
          'Content-Type': 'application/json'
        }
      }
    ),

  // Generate app-free public token (no app credentials required)
  generateAppFreeToken: (data: { livestream_id: string; user_id: string; permissions?: any; expires_in_hours?: number }) =>
    axios.post<{ success: boolean; data: any; message?: string }>(
      `${API_BASE_URL}/v1/livestreams/token/app-free`,
      data
    ),

  // Generate LiveKit token for public streams
  generateLiveKitToken: (data: { token: string; identity: string; is_publisher?: boolean }) =>
    axios.post<{ success: boolean; data: any; message?: string }>(`${API_BASE_URL}/v1/livestreams/livekit-token`, data),

  // Get public stream info for viewers (no authentication required)
  getPublicStreamInfo: (streamId: string) =>
    axios.get<{ success: boolean; data: any; message?: string }>(`${API_BASE_URL}/v1/livestreams/stream/${streamId}`),

  // Generate LiveKit viewer token for public streams (no authentication required)
  generatePublicViewerToken: (streamId: string, data: { identity: string }) =>
    axios.post<{ success: boolean; data: any; message?: string }>(`${API_BASE_URL}/v1/livestreams/stream/${streamId}/token`, data),

  // Start publishing (change status to live)
  startPublishing: (data: { token: string; livestream_id: string }) =>
    axios.post<{ success: boolean; data: any; message?: string }>(`${API_BASE_URL}/v1/livestreams/stream/start-publishing`, data),

  // End livestream with token authentication
  endStream: (data: { token: string; livestream_id: string }) =>
    axios.post<{ success: boolean; data: any; message?: string }>(`${API_BASE_URL}/v1/livestreams/stream/end`, data)
};

// Users API
export interface CreateUserRequest {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  is_active?: boolean;
  role_id?: string;
}

export interface UpdateUserRequest {
  first_name?: string;
  last_name?: string;
  email?: string;
  password?: string;
  is_active?: boolean;
  role_id?: string;
}

export const usersAPI = {
  list: (params?: { role_id?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.role_id) queryParams.append('role_id', params.role_id);
    const query = queryParams.toString();
    return api.get(`/users${query ? `?${query}` : ''}`);
  },
  get: (id: string) => api.get(`/users/${id}`),
  create: (data: CreateUserRequest) => api.post('/users', data),
  update: (id: string, data: UpdateUserRequest) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
};

// Roles API
export interface CreateRoleRequest {
  name: string;
  permissions: string[];
}

export interface UpdateRoleRequest {
  name?: string;
  permissions?: string[];
}

export const rolesAPI = {
  list: () => api.get('/roles'),
  get: (id: string) => api.get(`/roles/${id}`),
  create: (data: CreateRoleRequest) => api.post('/roles', data),
  update: (id: string, data: UpdateRoleRequest) => api.put(`/roles/${id}`, data),
  delete: (id: string) => api.delete(`/roles/${id}`),
};

export default api;
